import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtMXN } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Modal, In, Sel } from "./Projects";

const VENDOR_CATEGORIES = [
  { value: "materiales", label: "Materiales" },
  { value: "servicios", label: "Servicios" },
  { value: "equipo", label: "Equipo" },
  { value: "transporte", label: "Transporte" },
  { value: "otros", label: "Otros" },
];

const empty = { name: "", rfc: "", contact_name: "", phone: "", email: "", address: "", bank_account: "", category: "materiales", payment_terms_days: 30, notes: "", active: true };

export default function Vendors() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [overview, setOverview] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const canManage = ["admin", "contador", "auxiliar"].includes(user?.role);

  const load = async () => {
    const { data } = await api.get("/vendors"); setRows(data);
    try { const { data: ov } = await api.get("/vendors/overview"); setOverview(ov); } catch { /* roles sin acceso */ }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (v) => { setEditing(v.id); setForm({ ...empty, ...v }); setOpen(true); };
  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, payment_terms_days: Number(form.payment_terms_days || 0) };
    if (typeof payload.bank_account === "string" && payload.bank_account.includes("•")) delete payload.bank_account;
    Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
    try {
      if (editing) await api.patch(`/vendors/${editing}`, payload);
      else await api.post("/vendors", payload);
      setOpen(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el proveedor");
    }
  };
  const remove = async (id) => { if (!window.confirm("¿Eliminar proveedor?")) return; await api.delete(`/vendors/${id}`); load(); };

  return (
    <div data-testid="vendors-page">
      <PageHeader subtitle="Directorio de proveedores" title="Proveedores"
        actions={canManage && (
          <button data-testid="new-vendor-btn" onClick={startNew} className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest">
            <Plus className="w-4 h-4" /> Nuevo proveedor
          </button>
        )} />
      <div className="px-4 lg:px-8 py-6">
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="vendors-table">
            <thead className="panel-header">
              <tr className="text-left">
                <Th>Nombre</Th><Th>RFC</Th><Th>Categoría</Th><Th>Contacto</Th><Th>Teléfono</Th><Th right>Crédito</Th><Th>Activo</Th><Th right>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-slate-500">Sin proveedores aún.</td></tr>}
              {rows.map((v) => (
                <tr key={v.id} data-testid={`vendor-row-${v.id}`} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                  <Td>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-sm flex items-center justify-center" style={{ background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.35)" }}>
                        <Building2 className="w-3.5 h-3.5" style={{ color: "#A5B4FC" }} />
                      </div>
                      <div>
                        <Link to={`/vendors/${v.id}`} data-testid={`vendor-link-${v.id}`} className="font-semibold text-slate-100 hover:underline" style={{ color: "var(--cyan)" }}>{v.name}</Link>
                        <div className="flex gap-1.5 mt-0.5">
                          {overview[v.id]?.saldo > 0 && <span className="text-[9px] font-mono px-1 rounded-sm" style={{ border: "1px solid #f59e0b", color: "#f59e0b" }}>debe {fmtMXN(overview[v.id].saldo)}</span>}
                          {overview[v.id]?.pending_invoices > 0 && <span className="text-[9px] font-mono px-1 rounded-sm" style={{ border: "1px solid var(--border)", color: "var(--text-dim)" }}>{overview[v.id].pending_invoices} sin factura</span>}
                        </div>
                      </div>
                    </div>
                  </Td>
                  <Td className="font-mono text-xs text-slate-300">{v.rfc || "—"}</Td>
                  <Td><span className="capitalize text-xs px-2 py-0.5 rounded-sm" style={{ background: "var(--bg-2)", color: "var(--cyan)" }}>{v.category}</span></Td>
                  <Td className="text-xs text-slate-300">{v.contact_name || "—"}</Td>
                  <Td className="font-mono text-xs text-slate-300">{v.phone || "—"}</Td>
                  <Td right className="font-mono text-slate-300">{v.payment_terms_days}d</Td>
                  <Td>{v.active ? <span className="text-xs font-semibold" style={{ color: "var(--green)" }}>SÍ</span> : <span className="text-slate-500 text-xs">NO</span>}</Td>
                  <Td right>
                    {canManage && <button data-testid={`edit-vendor-${v.id}`} onClick={() => startEdit(v)} className="text-xs font-semibold hover:underline mr-3" style={{ color: "var(--cyan)" }}>EDITAR</button>}
                    {user?.role === "admin" && <button data-testid={`delete-vendor-${v.id}`} onClick={() => remove(v.id)} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)} title={editing ? "Editar proveedor" : "Nuevo proveedor"}>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <In label="Nombre comercial" testId="vendor-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <In label="RFC" testId="vendor-rfc" value={form.rfc} onChange={(v) => setForm({ ...form, rfc: v.toUpperCase() })} required={false} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label="Razón social" testId="vendor-legal-name" value={form.legal_name || ""} onChange={(v) => setForm({ ...form, legal_name: v })} required={false} />
              <In label="Régimen fiscal" testId="vendor-regime" value={form.tax_regime || ""} onChange={(v) => setForm({ ...form, tax_regime: v })} required={false} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label="Contacto" testId="vendor-contact" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} required={false} />
              <In label="Teléfono" testId="vendor-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required={false} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label="Email" testId="vendor-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required={false} />
              <In label="Dirección" testId="vendor-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} required={false} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Sel label="Categoría" testId="vendor-category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={VENDOR_CATEGORIES} />
              <In label="Días de crédito" testId="vendor-terms" type="number" value={form.payment_terms_days} onChange={(v) => setForm({ ...form, payment_terms_days: v })} required={false} />
              <In label="Cuenta bancaria" testId="vendor-bank" value={form.bank_account} onChange={(v) => setForm({ ...form, bank_account: v })} required={false} />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Notas</label>
              <textarea data-testid="vendor-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" data-testid="save-vendor-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Guardar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const Th = ({ children, right }) => <th className={`label-mono px-4 py-3 ${right ? "text-right" : ""}`}>{children}</th>;
const Td = ({ children, right, className = "" }) => <td className={`px-4 py-3 align-top ${right ? "text-right" : ""} ${className}`}>{children}</td>;
